import { EditorState } from 'draft-js';

import { MNHotInstance } from '../../bridge/tableHotInstanceTypes';
import { MilanoteHotPlugin } from './MilanoteHotPlugin';

// Utils
import { isFormula } from '../../bridge/utils/dataUtils/formula/tableFormulaPredicateUtils';
import { createFormulaPickerSession, type FormulaPickerSession } from '../../bridge/plugins/formulaAutocompleteSessionUtils';
import type { FormulaPickerRef } from '../useFormulaPickerController';

export const DRAFTJS_FORMULA_AUTOCOMPLETE_PLUGIN_NAME = 'DraftJSmilanoteFormulaAutocomplete';

type PluginSettings = {
    formulaPickerRef: React.RefObject<FormulaPickerRef>;
};

class DraftJSFormulaAutocompleteHotPlugin extends MilanoteHotPlugin<PluginSettings> {
    private formulaDropdownSession: FormulaPickerSession | null;
    private cellText: string | null;

    static get PLUGIN_KEY(): string {
        return DRAFTJS_FORMULA_AUTOCOMPLETE_PLUGIN_NAME;
    }

    constructor(hotInstance: MNHotInstance) {
        super(hotInstance);

        this.formulaDropdownSession = null;
        this.cellText = null;
    }

    /**
     * Enable the plugin.
     */
    enablePlugin(): void {
        this.addHook('afterBeginEditing', () => this.onAfterBeginEditing());
        this.addHook('afterSelection', () => this.onAfterSelection());

        // @ts-ignore - The official typings use TiptapContent now
        this.addMilanoteHook('onMilanoteCellEditorChange', (editorState: EditorState) =>
            this.onMilanoteCellEditorChange(editorState),
        );

        super.enablePlugin();
    }

    /**
     * Update the plugin.
     */
    updatePlugin(): void {
        this.disablePlugin();
        this.enablePlugin();

        super.updatePlugin();
    }

    /******************
     * PLUGIN HOOKS
     ******************/

    onAfterBeginEditing(): void {
        this.formulaDropdownSession = null;
        this.cellText = null;
    }

    onAfterSelection(): void {
        this.removeDropdown();
    }

    onMilanoteCellEditorChange(editorState: EditorState): void {
        const cellText = editorState.getCurrentContent().getPlainText();
        const cursorOffset = editorState.getSelection().getStartOffset();

        if (cellText === this.cellText) {
            this.onMilanoteCellEditorSelectionChange(cursorOffset);
            return;
        }

        this.cellText = cellText;

        if (!isFormula(cellText)) {
            this.removeDropdown();
            return;
        }

        if (!this.formulaDropdownSession && cellText === '=') {
            this.openFormulaPicker(cellText, cursorOffset);
            return;
        }

        this.updateDropdownForCellText(cellText, cursorOffset);
    }

    onMilanoteCellEditorSelectionChange(cursorOffset = this.getCursorOffset()): void {
        if (!this.formulaDropdownSession || cursorOffset === undefined) return;

        const { queryStartOffset, queryEndOffset } = this.formulaDropdownSession;
        const isCursorWithinQueryRange = cursorOffset >= queryStartOffset && cursorOffset <= queryEndOffset;
        if (isCursorWithinQueryRange) return;

        this.removeDropdown();
    }

    /******************
     * HELPER FUNCTIONS
     ******************/

    updateDropdownForCellText(newCellContent: string, cursorOffset = this.getCursorOffset()): void {
        if (cursorOffset === undefined) return this.removeDropdown();

        const { formulaPickerRef } = this.getConfig();
        if (!formulaPickerRef?.current) return;

        if (!this.formulaDropdownSession || cursorOffset < this.formulaDropdownSession.queryStartOffset) {
            return this.removeDropdown();
        }

        const query = newCellContent.slice(this.formulaDropdownSession.queryStartOffset, cursorOffset);

        this.formulaDropdownSession = {
            ...this.formulaDropdownSession,
            queryEndOffset: cursorOffset,
        };

        formulaPickerRef.current.setFormulaPickerQuery(query);
    }

    openFormulaPicker(cellContent: string, cursorOffset = this.getCursorOffset() ?? cellContent.length): void {
        this.formulaDropdownSession = createFormulaPickerSession(cellContent, cursorOffset);
        this.updateDropdownForCellText(cellContent, cursorOffset);
    }

    private getCursorOffset(): number | undefined {
        return this.milanoteHot.getMNEditor()?.getPlainTextSelection()?.from;
    }

    removeDropdown(): void {
        const { formulaPickerRef } = this.getConfig();
        if (!formulaPickerRef?.current) return;

        this.formulaDropdownSession = null;
        this.cellText = null;

        formulaPickerRef.current.hideFormulaPicker();
    }

    /**
     * Destroy the plugin.
     */
    destroy(): void {
        this.removeDropdown();
        super.destroy();
    }
}

export default DraftJSFormulaAutocompleteHotPlugin;
